import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Users, ArrowLeft, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { RANKS } from "../lib/api";
import { createLiveSocket } from "../lib/liveSocket";
import { sfx } from "../lib/sound";

export const LiveVoterScreen = ({ code, onExit }) => {
  const { t, dict } = useLang();
  const [phase, setPhase] = useState("connecting"); // connecting|waiting|voting|voted|reveal|over|notfound|hostleft
  const [meta, setMeta] = useState(null);
  const [viewers, setViewers] = useState(0);
  const [vote, setVote] = useState(null);
  const [counts, setCounts] = useState([0, 0]);
  const [myChoice, setMyChoice] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [result, setResult] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const s = createLiveSocket();
    socketRef.current = s;
    s.on("connect", () => {
      s.emit("viewer_join", { code }, (res) => {
        if (!res?.ok) { setPhase("notfound"); return; }
        setMeta(res);
        setViewers(res.viewers || 0);
        if (res.voting && res.vote) {
          setVote(res.vote);
          setCounts(res.vote.counts || []);
          setPhase("voting");
        } else {
          setPhase("waiting");
        }
      });
    });
    s.on("viewers", (d) => setViewers(d.count));
    s.on("vote_open", (d) => {
      setReveal(null);
      setMyChoice(null);
      setVote(d);
      setCounts(d.choix.map(() => 0));
      setPhase("voting");
      try { sfx.voteOpen(); } catch (e) {}
    });
    s.on("tally", (d) => setCounts(d.counts));
    s.on("vote_result", (d) => { setReveal(d); setPhase("reveal"); try { sfx.reveal(); } catch (e) {} });
    s.on("game_over", (d) => { setResult(d.result); setPhase("over"); try { sfx.victory(); } catch (e) {} });
    s.on("host_left", () => setPhase("hostleft"));
    return () => s.disconnect();
  }, [code]);

  const castVote = (i) => {
    if (myChoice !== null) return;
    setMyChoice(i);
    setPhase("voted");
    socketRef.current.emit("viewer_vote", { choiceIndex: i });
    try { sfx.click(); } catch (e) {}
  };

  const REACTIONS = ["🔥", "😂", "😱", "❤️", "👑", "💀"];
  const react = (emoji) => {
    socketRef.current && socketRef.current.emit("viewer_reaction", { emoji });
  };

  const total = counts.reduce((a, b) => a + b, 0);
  const pct = (i) => (total > 0 ? Math.round(((counts[i] || 0) / total) * 100) : 0);
  const rankColor = result ? RANKS[result.rank] || RANKS.anonyme : null;

  return (
    <div className="flex min-h-full flex-col px-5 py-6" data-testid="live-voter-screen">
      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#f5576c]">
        <span className="h-2 w-2 animate-pulse rounded-full bg-[#f5576c]" /> {t("live")}
        {meta && <span className="ml-auto flex items-center gap-1.5 text-white/60"><Users size={14} className="text-[#f093fb]" />{viewers}</span>}
      </div>

      {phase === "connecting" && (
        <div className="mt-24 text-center text-white/60" data-testid="live-connecting">{t("liveConnecting")}</div>
      )}

      {phase === "notfound" && (
        <div className="mt-24 text-center">
          <p className="text-white/70" data-testid="live-notfound">{t("liveRoomNotFound")}</p>
          <button onClick={onExit} className="mt-6 rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white/85">{t("liveJoin")}</button>
        </div>
      )}

      {phase === "hostleft" && (
        <div className="mt-24 text-center">
          <p className="text-white/70" data-testid="live-hostleft">{t("liveHostLeft")}</p>
          <button onClick={onExit} className="mt-6 rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white/85">OK</button>
        </div>
      )}

      {phase === "waiting" && (
        <div className="mt-24 text-center">
          <Radio size={48} className="mx-auto animate-pulse text-[#f5576c]" />
          <p className="mt-6 text-lg font-semibold text-white" data-testid="live-waiting">{t("liveWaitingHost")}</p>
          {meta && <p className="mt-2 text-sm text-white/50">{meta.domaineNom}</p>}
        </div>
      )}

      {(phase === "voting" || phase === "voted") && vote && (
        <div className="mt-6" data-testid="live-voting">
          <div className="flex items-center justify-between text-sm text-white/50">
            <span>{t("liveQuestion")} {(vote.index ?? 0) + 1}/{vote.total}</span>
            <span className="font-semibold text-[#f5576c]">{phase === "voted" ? t("liveVoted") : t("liveVoteNow")}</span>
          </div>
          {vote.rare && (
            <span className="mt-3 inline-block rounded-full bg-gradient-to-r from-[#ffd700] to-[#f5576c] px-3 py-1 font-display text-[11px] font-bold uppercase tracking-widest text-black">
              {vote.rarete === "legendaire" ? t("rareLegendary") : t("rareEpic")}
            </span>
          )}
          <p className="mt-3 font-display text-xl font-bold leading-snug text-white" data-testid="live-voter-question">{vote.texte}</p>
          <div className="mt-6 space-y-4">
            {vote.choix.map((label, i) => (
              <motion.button key={i} whileTap={{ scale: 0.97 }} onClick={() => castVote(i)}
                disabled={phase === "voted"}
                data-testid={`live-vote-choice-${i}`}
                className={`relative w-full overflow-hidden rounded-2xl border p-5 text-left font-display text-lg font-bold transition-all ${
                  myChoice === i ? "border-[#38ef7d] bg-[#38ef7d]/15 text-white"
                    : phase === "voted" ? "border-white/10 text-white/50" : "border-white/20 bg-white/5 text-white"
                }`}>
                {phase === "voted" && (
                  <span className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#f093fb]/30 to-[#f5576c]/30" style={{ width: `${pct(i)}%` }} />
                )}
                <span className="relative flex items-center justify-between gap-3">
                  <span>{label}</span>
                  {myChoice === i && <Check size={22} className="text-[#38ef7d]" />}
                  {phase === "voted" && myChoice !== i && <span className="text-sm text-white/50">{pct(i)}%</span>}
                </span>
              </motion.button>
            ))}
          </div>
          <div className="mt-6">
            <p className="mb-2 text-center text-xs uppercase tracking-widest text-white/40">{t("liveReactions")}</p>
            <div className="flex justify-center gap-2" data-testid="live-reaction-bar">
              {REACTIONS.map((e) => (
                <button key={e} onClick={() => react(e)}
                  data-testid={`live-react-${e}`}
                  className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/12 bg-white/5 text-2xl transition-transform active:scale-90 hover:bg-white/10">
                  {e}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {phase === "reveal" && reveal && vote && (
        <div className="mt-6" data-testid="live-voter-reveal">
          <p className="text-center text-sm font-semibold text-[#38ef7d]">{t("liveMajorityChose")}</p>
          <p className="mt-4 rounded-2xl border border-[#38ef7d] bg-[#38ef7d]/10 p-5 text-center font-display text-lg font-bold text-white">
            {vote.choix[reveal.chosen]}
          </p>
          <p className="mt-3 text-center text-sm text-white/60">{reveal.feedback}</p>
        </div>
      )}

      {phase === "over" && result && (
        <div className="mt-16 text-center" data-testid="live-voter-over">
          <p className="text-sm uppercase tracking-widest text-white/50">{t("liveFinished")}</p>
          <p className="mt-1 text-sm text-white/60">{t("liveCollectiveDestiny")}</p>
          <div className="mt-6 font-display text-5xl font-black uppercase" style={{ color: rankColor?.c1 }}>
            {dict.ranks?.[result.rank] || result.rank}
          </div>
          <div className="mt-3 font-display text-4xl font-black text-white">{(result.score || 0).toLocaleString()}</div>
          <p className="text-xs uppercase tracking-widest text-white/40">points</p>
          <button onClick={onExit} className="mt-8 w-full rounded-2xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] py-4 font-bold text-white" data-testid="live-voter-home">
            {t("play")}
          </button>
        </div>
      )}
    </div>
  );
};
